/**
 * OPFS (Origin Private File System) layer for binary file storage.
 *
 * Design decisions:
 * - All reads/writes happen via sync handles ONLY inside Web Workers (createSyncAccessHandle
 *   is not available on the main thread). The main thread uses the async API.
 * - Every handle.close() is wrapped in finally to prevent stale locks, which in some
 *   browsers persist until the page is reloaded.
 * - Files are namespaced under a `nitido/` root directory to avoid collisions.
 */

import * as Comlink from 'comlink'

const ROOT_DIR = 'nitido'

let opfsWorkerProxy: Comlink.Remote<{
  writeToOPFSSync: (path: string, data: ArrayBuffer) => Promise<void>
}> | null = null

function getOpfsWorker() {
  if (!opfsWorkerProxy) {
    const worker = new Worker(new URL('../../workers/opfs.worker.ts', import.meta.url), {
      type: 'module',
    })
    opfsWorkerProxy = Comlink.wrap(worker)
  }
  return opfsWorkerProxy
}

async function getRootDir(): Promise<FileSystemDirectoryHandle> {
  const opfsRoot = await navigator.storage.getDirectory()
  return opfsRoot.getDirectoryHandle(ROOT_DIR, { create: true })
}

function categoryDir(opfsPath: string): { dir: string; filename: string } {
  const parts = opfsPath.split('/')
  return {
    dir: parts.slice(0, -1).join('/'),
    filename: parts[parts.length - 1],
  }
}

async function resolveDir(
  root: FileSystemDirectoryHandle,
  relativePath: string,
): Promise<FileSystemDirectoryHandle> {
  const parts = relativePath.split('/').filter(Boolean)
  let current = root
  for (const part of parts) {
    current = await current.getDirectoryHandle(part, { create: true })
  }
  return current
}

/**
 * Writes an ArrayBuffer to OPFS at the given path (relative to nitido/ root).
 * Cross-browser implementation:
 * - In Dedicated Workers (Chrome, Safari, Firefox): uses createSyncAccessHandle (fastest).
 * - In Main Thread on Chromium/Firefox: uses createWritable.
 * - In Main Thread on Safari (where createWritable does not exist): delegates to opfs.worker.
 */
export async function writeToOPFS(opfsPath: string, data: ArrayBuffer): Promise<void> {
  const isDedicatedWorker =
    typeof self !== 'undefined' &&
    typeof WorkerGlobalScope !== 'undefined' &&
    self instanceof WorkerGlobalScope

  const root = await getRootDir()
  const { dir, filename } = categoryDir(opfsPath)
  const dirHandle = await resolveDir(root, dir)
  const fileHandle = await dirHandle.getFileHandle(filename, { create: true })

  // 1. Inside dedicated workers: use synchronous handle (universal across Safari/Chrome/Firefox)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (isDedicatedWorker && typeof (fileHandle as any).createSyncAccessHandle === 'function') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const accessHandle = await (fileHandle as any).createSyncAccessHandle()
    try {
      accessHandle.truncate(0)
      accessHandle.write(new Uint8Array(data), { at: 0 })
      accessHandle.flush()
    } finally {
      accessHandle.close()
    }
    return
  }

  // 2. On main thread in Chromium / Firefox: use standard createWritable
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (typeof (fileHandle as any).createWritable === 'function') {
    const writable = await fileHandle.createWritable()
    try {
      await writable.write(data)
    } finally {
      await writable.close()
    }
    return
  }

  // 3. On main thread in Safari: delegate to worker using createSyncAccessHandle
  const worker = getOpfsWorker()
  await worker.writeToOPFSSync(opfsPath, Comlink.transfer(data, [data]))
}

/**
 * Reads a file from OPFS. Returns null if the file does not exist.
 */
export async function readFromOPFS(opfsPath: string): Promise<ArrayBuffer | null> {
  try {
    const root = await getRootDir()
    const { dir, filename } = categoryDir(opfsPath)
    const dirHandle = await resolveDir(root, dir)
    const fileHandle = await dirHandle.getFileHandle(filename)
    const file = await fileHandle.getFile()
    return file.arrayBuffer()
  } catch {
    // File not found — expected for unprocessed files
    return null
  }
}

/**
 * Reads a file from OPFS as a Blob (for object URL creation without full copy to RAM).
 */
export async function readBlobFromOPFS(opfsPath: string): Promise<Blob | null> {
  try {
    const root = await getRootDir()
    const { dir, filename } = categoryDir(opfsPath)
    const dirHandle = await resolveDir(root, dir)
    const fileHandle = await dirHandle.getFileHandle(filename)
    return fileHandle.getFile()
  } catch {
    return null
  }
}

/**
 * Deletes a file from OPFS. Safe to call even if file doesn't exist.
 */
export async function deleteFromOPFS(opfsPath: string): Promise<void> {
  try {
    const root = await getRootDir()
    const { dir, filename } = categoryDir(opfsPath)
    const dirHandle = await resolveDir(root, dir)
    await dirHandle.removeEntry(filename)
  } catch {
    // Ignore — file may not exist
  }
}

/**
 * Deletes all files under a given directory prefix (e.g., "results/{id}").
 */
export async function deleteDirectoryFromOPFS(dirPath: string): Promise<void> {
  try {
    const root = await getRootDir()
    const parts = dirPath.split('/').filter(Boolean)
    if (parts.length === 0) return
    const parentParts = parts.slice(0, -1)
    const targetName = parts[parts.length - 1]
    const parentDir = await resolveDir(root, parentParts.join('/'))
    await parentDir.removeEntry(targetName, { recursive: true })
  } catch {
    // Ignore — directory may not exist
  }
}

/**
 * Requests persistent storage to prevent Safari from evicting OPFS data.
 * Should be called once on app initialization.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false
  const already = await navigator.storage.persisted()
  if (already) return true
  return navigator.storage.persist()
}

/**
 * Returns storage quota and usage in bytes.
 */
export async function getStorageEstimate(): Promise<{ quota: number; usage: number }> {
  const estimate = await navigator.storage.estimate()
  return {
    quota: estimate.quota ?? 0,
    usage: estimate.usage ?? 0,
  }
}
